/**
 * Générateur — Automatisme : utiliser le cosinus d'un angle d'un triangle
 * rectangle pour calculer une longueur ou la mesure d'un angle.
 * Attendu ID : 1090 | Niveau : 4ème | Domaine : Espace et géométrie, géométrie plane
 * Rubrique : AUTOMATISME (créé par le professeur, gen neuf du 2026-09-24).
 *
 * ── D'où vient l'intitulé ─────────────────────────────────────────────────
 * Au BO 2025, le cosinus est un OBJECTIF de 3e (cycle 4 l. 1106 « Connaitre
 * et utiliser les lignes trigonométriques dans le triangle rectangle :
 * cosinus, sinus, tangente ») ; le professeur le garde en 4e. Lignes servies,
 * qui font plafond : Eduscol, attendus de fin de 4e (2019) l. 393 (« le
 * cosinus d'un angle d'un triangle rectangle ») et l. 419 (« Dans un
 * triangle rectangle, il utilise le cosinus pour déterminer la mesure d'un
 * angle »), exemple de réussite l. 420-427 (l'échelle de 13 m posée à 5 m du
 * mur : environ 67°). Le sens « longueur » est le choix du prof (son cours
 * 19Cosinus : « 5 ÷ cos 30° ») : inférence, comme Thalès en 4e.
 *
 * ── Cadrage (sous-agent programme-maths 2026-09-24, décisions prof) ───────
 * AVEC calculatrice (décision prof 24/09) : contenu.calculatrice = 'cos' sur
 * toutes les cartes de calcul, la calculatrice de l'écran y montre les
 * touches cos et arccos, en degrés (décision prof du 24/09 au soir : « faut
 * rajouter cos dans la calculatrice ») ; rappel brouillon IMPÉRATIF sur les
 * quatre cartes de calcul depuis le 25/09 (règle par attendu, prof : « brouillon
 * impératif » sur l'échelle, « détaillé » sur les étapes), pas sur « Écrire le
 * cosinus » où rien ne se calcule (check du 25/09 au soir). Angles de 20° à 70°,
 * jamais 45° (côté opposé = côté adjacent : l'erreur principale disparaît)
 * ni 60° (la fiche coup de pouce, cos = 0,5) ; longueurs données entières de 3 à 12 cm, le côté
 * adjacent plus court que l'hypoténuse ; angle arrondi au DEGRÉ, longueur au
 * DIXIÈME, arrondis jamais ambigus (la valeur exacte à 0,05 au moins d'un
 * pivot, dernière décimale non nulle : règle de 189). Figure EXACTE
 * (figTriangle.js, sept orientations, angle droit codé, l'angle marqué d'un
 * arc, sa mesure ou « ? » en orange, les longueurs données sur les côtés).
 * Écriture du cours : « cos RVW̄ = VR / VW », l'angle nommé par ses trois
 * lettres. SACRIFIÉ (fiche à venir) : le triangle du cours (2 ; 1 ; 60°).
 * Les cartes cosinus de gen_189 déménagent ici (vague 4 : 189 les perd).
 *
 * ── Cartes ────────────────────────────────────────────────────────────────
 *   « Écrire le cosinus » (ecrire_qcm_unique) : figure, « Quelle égalité est
 *     vraie ? » ; adjacent / hypoténuse contre l'opposé, le rapport retourné,
 *     les deux côtés de l'angle droit. Le QCM de l'attendu.
 *   « Calculer un angle » (angle_texte) : le côté adjacent et l'hypoténuse,
 *     l'angle arrondi au degré.
 *   « Calculer une longueur » (longueur_texte) : l'angle et un côté, le
 *     côté adjacent (hypoténuse × cos) ou l'hypoténuse (adjacent ÷ cos), un
 *     tirage sur deux, arrondi au dixième.
 *   « Les étapes du calcul » (etapes_texte_trou_saisie) : la rédaction
 *     d'abord (prof 25/09 : « On sait que le triangle … est rectangle en ….
 *     Donc, dans ce triangle rectangle, le cosinus de l'angle … est égal au
 *     côté adjacent divisé par l'hypoténuse : »), puis les lignes du cours
 *     avec les nombres en cases, l'angle ou une longueur.
 *   « L'échelle » (echelle_texte) : l'angle entre le sol et l'échelle
 *     (Eduscol l. 420-427), une seule étape.
 *   Coupées (épreuve de Musk) : « Identifier les côtés » (redit par « Écrire
 *   le cosinus », dont les pièges sont les mêmes confusions), « Calculer un
 *   cosinus » et « Retrouver un angle » depuis un nombre nu (des touches de
 *   calculatrice, pas le cosinus d'un triangle), l'écriture en étiquettes
 *   (redit le QCM), « La valeur remarquable » (60°, c'est la fiche).
 *
 * ── Erreurs (familles nommées ; aucun item DEPP, inférence) ───────────────
 *   oppose_pour_adjacent (PRINCIPALE), rapport_inverse (hypoténuse sur
 *   adjacent, plus grand que 1), cote_de_l_angle_droit (les deux côtés de
 *   l'angle droit) ; en saisie : multiplie_toujours, divise_toujours,
 *   mode_radian, arrondi_tronque.
 *
 * Hors : le sinus et la tangente (3e) ; le dixième de degré et le centième de
 * cm ; les problèmes à deux outils (l'échelle hauteur puis angle, supprimée
 * de 189 le 24/09 au soir : la hauteur est dans 1088, l'angle ici).
 */
import {
    randInt, randomChoice, pickLettresSains, tex,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { svgTriangle, triangleRectangle, ORIENTATIONS } from '../../3eme/geometrie-plane/figTriangle';
import { svgEchelle } from './figContextes';

const ATTENDU_ID = 1090;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ECRIRE = 'Écrire le cosinus';
const L_ANGLE = 'Calculer un angle';
const L_LONGUEUR = 'Calculer une longueur';
const L_ETAPES = 'Les étapes du calcul';
const L_ECHELLE = 'L\'échelle';

const POOL = 'ABCDEFGHJKLMNPRSTUV'.split('');
const PRENOMS = ['Léna', 'Youri', 'Sacha', 'Nina', 'Timéo', 'Awa', 'Malo', 'Jade', 'Inès', 'Noam'];
// Rappel brouillon (règle prof du 2026-09-23, par attendu ; posé ici le 2026-09-25) : IMPÉRATIF sur
// les cinq formats, juste avant la question.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

export const cosDeg = (a) => Math.cos((a * Math.PI) / 180);
export const arccosDeg = (c) => (Math.acos(c) * 180) / Math.PI;
/** Un arrondi sûr : la valeur exacte est à 0,05 unité au moins du pivot (…,5). */
const loinDuPivot = (x) => Math.abs(Math.abs(x - Math.floor(x) - 0.5)) >= 0.05;
const fr1 = (x) => (Math.round(x * 10) / 10).toFixed(1).replace('.', ',');
const cote = (p, q) => [p, q].sort().join('');
/** Les angles admis : 20° à 70°, sans 45° ni 60°. */
const ANGLES = Array.from({ length: 51 }, (_, i) => 20 + i).filter((a) => a !== 45 && a !== 60);

/** Le triangle : R l'angle droit, V l'angle étudié, W le troisième sommet ; adj = VR, hyp = VW, opp = RW. */
const triangle = (adj, theta) => {
    const [R, V, W] = pickLettresSains(POOL, 3);
    const opp = adj * Math.tan((theta * Math.PI) / 180);
    const scene = triangleRectangle({ lettres: [R, V, W], droit: 0, a: adj, b: opp, deg: randomChoice(ORIENTATIONS) });
    const nom = randomChoice([`${R}${V}${W}`, `${V}${W}${R}`, `${W}${R}${V}`, `${R}${W}${V}`]);
    return { R, V, W, scene, nom, adj: cote(V, R), hyp: cote(V, W), opp: cote(R, W), angle: `${R}${V}${W}` };
};
const figure = (t, cotes, angleTexte, angleCherche) => imgElement(svgTriangle(
    { ...t.scene, cotes, angles: [{ sommet: t.V, texte: angleTexte, cherche: angleCherche }] },
    `Le triangle ${t.nom}, rectangle en ${t.R} ; l'angle en ${t.V} est marqué.`,
), 'large');
const chapeau = (t) => tex(`\\widehat{${t.angle}}`);
const cmTex = (nom, v) => `${tex(`${nom} = ${v}`)} cm`;
/** La calculatrice de l'écran, avec les touches cos et arccos (degrés) : lue par calculatriceAutorisee et calculatriceCosinus. */
const calculatrice = (payload) => ({ ...payload, contenu: { ...payload.contenu, calculatrice: 'cos' } });

// ─── Tirages ───────────────────────────────────────────────────────────────

/** Un angle cherché : adjacent et hypoténuse entiers (3..12), l'angle dans ANGLES à l'arrondi, arrondi sûr, jamais adj/hyp = 1/2. */
export const tirerAngle = () => {
    for (let essai = 0; essai < 500; essai++) {
        const hyp = randInt(4, 12); const adj = randInt(3, hyp - 1);
        if (2 * adj === hyp) continue;
        const theta = arccosDeg(adj / hyp);
        if (!loinDuPivot(theta) || !ANGLES.includes(Math.round(theta))) continue;
        return { adj, hyp, theta, rep: Math.round(theta) };
    }
    return { adj: 5, hyp: 8, theta: arccosDeg(5 / 8), rep: 51 };   // repli vérifié : 51,32°
};

/** Une longueur cherchée : l'angle (ANGLES) et un côté entier (3..12) ; adjacent (hyp × cos) ou hypoténuse (adj ÷ cos). */
export const tirerLongueur = (cherche) => {
    for (let essai = 0; essai < 500; essai++) {
        const theta = randomChoice(ANGLES); const donne = randInt(3, 12);
        const x = cherche === 'adj' ? donne * cosDeg(theta) : donne / cosDeg(theta);
        if (x > 30 || x < 1 || !loinDuPivot(x * 10) || Math.round(x * 10) % 10 === 0) continue;
        return { theta, donne, x, rep: fr1(x) };
    }
    // replis vérifiés : 8 × cos 40° = 6,128 ; 5 ÷ cos 35° = 6,104 (loin des pivots)
    return cherche === 'adj' ? { theta: 40, donne: 8, x: 8 * cosDeg(40), rep: '6,1' } : { theta: 35, donne: 5, x: 5 / cosDeg(35), rep: '6,1' };
};

// ─── Carte « Écrire le cosinus » (QCM) ─────────────────────────────────────

const genEcrire = () => {
    const theta = randomChoice(ANGLES); const adj = randInt(3, 8);
    const t = triangle(adj, theta);
    const eg = (num, den) => tex(`\\cos \\widehat{${t.angle}} = \\dfrac{${num}}{${den}}`);
    const bon = eg(t.adj, t.hyp);
    const faux = [
        { v: eg(t.opp, t.hyp), code: 'oppose_pour_adjacent' },
        { v: eg(t.hyp, t.adj), code: 'rapport_inverse' },
        { v: eg(t.adj, t.opp), code: 'cote_de_l_angle_droit' },
    ];
    // Pas de rappel brouillon : on choisit une écriture, rien ne se calcule (prof 2026-09-25).
    const texte = `<p>Le triangle ${tex(t.nom)} est rectangle en ${tex(t.R)}.</p><p><b>Quelle égalité est vraie ?</b></p>`;
    const payload = makeQcm([bon, ...faux.map((f) => f.v)], bon, texte, { ...opts(L_ECRIRE), elements_additionnels: figure(t, [], '', false) });
    payload._meta.params = { theta, R: t.R, V: t.V, W: t.W, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte « Calculer un angle » (saisie) ──────────────────────────────────

const genAngle = () => {
    const a = tirerAngle(); const t = triangle(a.adj, a.theta);
    const cotes = [{ de: t.V, a: t.R, texte: `${a.adj} cm` }, { de: t.V, a: t.W, texte: `${a.hyp} cm` }];
    const texte = `<p>Le triangle ${tex(t.nom)} est rectangle en ${tex(t.R)}, avec ${cmTex(t.adj, a.adj)} et ${cmTex(t.hyp, a.hyp)}.</p>`
        + RAPPEL_BROUILLON + `<p><b>Calcule la mesure de l'angle ${chapeau(t)}, arrondie au degré.</b></p>`;
    const payload = makeSaisie(String(a.rep), texte, { ...opts(L_ANGLE), suffixe: '°', elements_additionnels: figure(t, cotes, '?', true) });
    payload._meta.params = { ...a, R: t.R, V: t.V, W: t.W };
    // Calculatrice AUTORISÉE (décision prof 2026-09-24) : arccos, arrondi au degré.
    return calculatrice(payload);
};

// ─── Carte « Calculer une longueur » (saisie) ──────────────────────────────

const genLongueur = () => {
    const cherche = randomChoice(['adj', 'hyp']);
    const l = tirerLongueur(cherche);
    const adjDessin = cherche === 'adj' ? l.x : l.donne;
    const t = triangle(adjDessin, l.theta);
    const nomDonne = cherche === 'adj' ? t.hyp : t.adj; const nomCherche = cherche === 'adj' ? t.adj : t.hyp;
    const cotes = [
        { de: nomDonne[0], a: nomDonne[1], texte: `${l.donne} cm` },
        { de: nomCherche[0], a: nomCherche[1], texte: '?', cherche: true },
    ];
    const texte = `<p>Le triangle ${tex(t.nom)} est rectangle en ${tex(t.R)}, avec ${cmTex(nomDonne, l.donne)} et ${tex(`\\widehat{${t.angle}} = ${l.theta}^{\\circ}`)}.</p>`
        + RAPPEL_BROUILLON + `<p><b>Calcule ${tex(nomCherche)}, arrondie au dixième de cm.</b></p>`;
    const payload = makeSaisie(l.rep, texte, { ...opts(L_LONGUEUR), suffixe: 'cm', elements_additionnels: figure(t, cotes, `${l.theta}°`, false) });
    payload._meta.params = { ...l, cherche, R: t.R, V: t.V, W: t.W };
    // Calculatrice AUTORISÉE (décision prof 2026-09-24) : cosinus d'un angle, arrondi au dixième.
    return calculatrice(payload);
};

// ─── Carte « Les étapes du calcul » (saisie guidée) ────────────────────────

/** La rédaction complète (prof 25/09), écrite au-dessus des lignes à compléter : l'hypothèse, puis la définition du cosinus. */
const redaction = (t) => `<p>On sait que le triangle ${tex(t.nom)} est rectangle en ${tex(t.R)}. `
    + `Donc, dans ce triangle rectangle, le cosinus de l'angle ${chapeau(t)} est égal au côté adjacent divisé par l'hypoténuse :</p>`;

const genEtapes = () => {
    const cas = randomChoice(['angle', 'adj', 'hyp']);
    const formule = (t) => `$\\cos \\widehat{${t.angle}} = \\dfrac{${t.adj}}{${t.hyp}}$`;
    if (cas === 'angle') {
        const a = tirerAngle(); const t = triangle(a.adj, a.theta);
        const cotes = [{ de: t.V, a: t.R, texte: `${a.adj} cm` }, { de: t.V, a: t.W, texte: `${a.hyp} cm` }];
        const lignes = [
            formule(t),
            `$\\cos \\widehat{${t.angle}} = \\dfrac{{{${a.adj}}}}{{{${a.hyp}}}}$`,
            `$\\widehat{${t.angle}} \\approx {{${a.rep}}}^{\\circ}$`,
        ];
        const texte = `<p>Le triangle ${tex(t.nom)} est rectangle en ${tex(t.R)}, avec ${cmTex(t.adj, a.adj)} et ${cmTex(t.hyp, a.hyp)}.</p>`
            + RAPPEL_BROUILLON + `<p><b>Complète le calcul de l'angle ${chapeau(t)}, arrondi au degré.</b></p>` + redaction(t);
        const payload = makeTexteTrou(lignes.join('\n'), texte, { ...opts(L_ETAPES), mode: 'saisie', elements_additionnels: figure(t, cotes, '?', true) });
        payload._meta.params = { cas, ...a, R: t.R, V: t.V, W: t.W };
        return calculatrice(payload);
    }
    const l = tirerLongueur(cas);
    const t = triangle(cas === 'adj' ? l.x : l.donne, l.theta);
    const nomDonne = cas === 'adj' ? t.hyp : t.adj; const nomCherche = cas === 'adj' ? t.adj : t.hyp;
    const cotes = [
        { de: nomDonne[0], a: nomDonne[1], texte: `${l.donne} cm` },
        { de: nomCherche[0], a: nomCherche[1], texte: '?', cherche: true },
    ];
    const cosT = `\\cos ${l.theta}^{\\circ}`;
    const lignes = [formule(t)];
    if (cas === 'adj') {
        lignes.push(
            `$${cosT} = \\dfrac{${t.adj}}{{{${l.donne}}}}$`,
            `$${t.adj} = ${t.hyp} \\times ${cosT}$`,
            `$${t.adj} \\approx {{${l.rep}}}\\text{ cm}$`,
        );
    } else {
        lignes.push(
            `$${cosT} = \\dfrac{{{${l.donne}}}}{${t.hyp}}$`,
            `$${t.hyp} = \\dfrac{${t.adj}}{${cosT}}$`,
            `$${t.hyp} \\approx {{${l.rep}}}\\text{ cm}$`,
        );
    }
    const texte = `<p>Le triangle ${tex(t.nom)} est rectangle en ${tex(t.R)}, avec ${cmTex(nomDonne, l.donne)} et ${tex(`\\widehat{${t.angle}} = ${l.theta}^{\\circ}`)}.</p>`
        + RAPPEL_BROUILLON + `<p><b>Complète le calcul de ${tex(nomCherche)}, arrondie au dixième de cm.</b></p>` + redaction(t);
    const payload = makeTexteTrou(lignes.join('\n'), texte, { ...opts(L_ETAPES), mode: 'saisie', elements_additionnels: figure(t, cotes, `${l.theta}°`, false) });
    payload._meta.params = { cas, ...l, R: t.R, V: t.V, W: t.W };
    return calculatrice(payload);
};

// ─── Carte « L'échelle » (saisie, contexte) ────────────────────────────────

/**
 * Les échelles (en m) : L la longueur, d la distance du pied au mur (au
 * dixième) ; l'angle avec le sol de 60° à 75° (une échelle se pose raide, Eduscol
 * recommande 65° à 75° ; au-delà de 75° le « ? » de l'angle touchait l'échelle, vu en PNG),
 * arrondi sûr ; jamais l'échelle d'Eduscol (13 m, 5 m).
 */
export const ECHELLES = [2.5, 3, 3.5, 4, 5, 6, 7, 8].flatMap((L) => Array.from({ length: Math.round(L * 5) }, (_, i) => (i + 1) / 10)
    .filter((d) => d < L)
    .map((d) => ({ L, d, theta: arccosDeg(d / L) }))
    .filter((e) => e.theta >= 60 && e.theta <= 75 && loinDuPivot(e.theta)));
const frM = (x) => String(x).replace('.', ',');

const genEchelle = () => {
    const e = randomChoice(ECHELLES);
    const prenom = randomChoice(PRENOMS);
    const h = Math.sqrt(e.L * e.L - e.d * e.d);
    const texte = `<p>${prenom} pose une échelle de ${frM(e.L)} m contre un mur vertical, sur un sol horizontal. Le pied de l'échelle est à ${frM(e.d)} m du mur.</p>`
        + RAPPEL_BROUILLON + '<p><b>Quel angle l\'échelle fait-elle avec le sol ? Donne l\'arrondi au degré.</b></p>';
    const figureEchelle = imgElement(svgEchelle({ d: e.d, h, mesures: { d: `${frM(e.d)} m`, L: `${frM(e.L)} m` }, cherche: 'angle', angle: '?' }), 'large');
    const payload = makeSaisie(String(Math.round(e.theta)), texte, { ...opts(L_ECHELLE), suffixe: '°', elements_additionnels: figureEchelle });
    payload._meta.params = { L: e.L, d: e.d, theta: e.theta };
    // Calculatrice AUTORISÉE (décision prof 2026-09-24) : arccos, arrondi au degré.
    return calculatrice(payload);
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'angles de 20° à 70° sans 45° ni 60°, longueurs données entières de 3 à 12 cm, arrondis sûrs (0,05 d\'un pivot au moins, dernière décimale non nulle), jamais le triangle de la fiche (2 ; 1 ; 60°)',
        'figure exacte, angle droit codé, angle marqué ; lettres saines en KaTeX ; l\'angle écrit par ses trois lettres',
    ],
    ecrire_qcm_unique: 'oppose_pour_adjacent (PRINCIPALE), rapport_inverse (hypoténuse sur adjacent), cote_de_l_angle_droit (adjacent sur opposé) ; jamais 45° (opposé et adjacent égaux)',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    ecrire_qcm_unique: genEcrire,
    angle_texte: genAngle,
    longueur_texte: genLongueur,
    etapes_texte_trou_saisie: genEtapes,
    echelle_texte: genEchelle,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', texte: 'Saisie', texte_trou_saisie: 'Saisie guidée' };
const buildFormats = (prefix) => Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
    .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'ecrire', label: L_ECRIRE, formatsList: buildFormats('ecrire') },
    { id: 'angle', label: L_ANGLE, formatsList: buildFormats('angle') },
    { id: 'longueur', label: L_LONGUEUR, formatsList: buildFormats('longueur') },
    { id: 'etapes', label: L_ETAPES, formatsList: buildFormats('etapes') },
    { id: 'echelle', label: L_ECHELLE, formatsList: buildFormats('echelle') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
